/**
 * Creating an enquiry: the wire body, and the sequence around it.
 *
 * ---- The rule this module exists to enforce ----
 *
 * `enquiry.create_enquiry` returning a name is the point of no return. Past it the document is on
 * the board, and the two steps that follow — assigning the purchaser, reading the document back —
 * are FOLLOW-UPS to something that already happened.
 *
 * They used to be awaited bare. A failure in either rejected the whole mutation, so the screen
 * showed an error and did not navigate, while the enquiry sat there created and unreachable from
 * the dialog that had just made it. From the outside that is indistinguishable from "manual
 * enquiry creation does not work", and it leaves an orphan behind every time.
 *
 * Assignment is a lesser act than creation: an unassigned enquiry is a working enquiry anybody can
 * pick up, and the RFQ header card sets the purchaser in one field. A failed read-back is lesser
 * still — the screen we navigate to fetches the document itself. So both degrade to a `warning`
 * carried back BESIDE the enquiry, and only a genuine failure to create throws.
 *
 * ---- Why the API is injected ----
 *
 * So the sequence above is testable without mocking `fetch` (this repo has no `renderHook` — see
 * `useEnquiries.test.ts`). `createEnquiryFlow.test.ts` drives each leg to failure in turn; those
 * tests fail the moment a follow-up is awaited bare again.
 */

import { toEnquiry, type WireEnquiryDetail } from './useEnquiries'
// TYPE-ONLY, and that direction matters: `useEnquiryMutations.ts` imports `createEnquiryFlow` as a
// value, so a value import back would close a runtime cycle. A type import is erased at compile
// time, which lets these two interfaces stay next to the long-form evidence notes that explain
// them (`company` accepted after all, `mail_id` round-trips) instead of being split from it.
import type { NewEnquiryHeader } from './useEnquiryMutations'
import type { Enquiry, NewEnquiryItem } from '../types'

/**
 * Just enough of `bosun.ts` to run the sequence — `callGet`/`callPost` satisfy it as they are, and
 * the members keep THOSE names on purpose: `server/allowlistScan.ts` finds proxy-allowlisted
 * methods by looking for a `callPost`/`callGet` token followed by literal arguments. Named `post`
 * and `get`, this file's two calls became invisible to it, and `enquiry.create_enquiry` silently
 * dropped out of the scan — the exact staleness that scanner exists to catch.
 */
export interface EnquiryWriteApi {
  callPost: <T>(module: string, method: string, body?: unknown) => Promise<T>
  callGet: <T>(module: string, method: string, params?: Record<string, unknown>) => Promise<T>
}

/** `create_enquiry` answers with the new docname — at the top level on live, nested on the mock. */
interface WireCreateEnquiryResult {
  name?: string
  data?: { name?: string }
}

export function toNewItemWireRow(item: NewEnquiryItem): Record<string, unknown> {
  const row: Record<string, unknown> = {
    item_code: item.item_code,
    qty: item.qty,
    uom: item.uom,
    route: 'rfq',
    create_rfq: 1,
    suppliers: item.suppliers ?? [],
  }
  // Only send what the caller actually has. An absent key and an explicit `null` are different
  // things to Frappe, and inventing a description would put our text on the customer's line.
  if (item.raw_text !== undefined) row.raw_text = item.raw_text
  if (item.description !== undefined) row.description = item.description
  // The customer's own unit, for next time — see `EnquiryItem.customer_uom`. Only when known.
  if (item.customer_uom) row.customer_uom = item.customer_uom
  // The model's own confidence. Omitted when the caller has none (a manually added line has no
  // model behind it) rather than sent as 0, which would read as "the AI was certain it was wrong".
  if (item.confidence !== undefined) row.extraction_confidence = item.confidence
  return row
}

/**
 * The exact `create_enquiry` wire body — a pure function a test can pin without mocking `fetch`.
 * `customer_currency`/`po_currency` are deliberately NOT parameters: `create_enquiry`'s documented
 * body has no field for either, and `CreateEnquiryBar` never asks for them. `company` DOES have a
 * home and is sent when present; `EnquiryHeaderCard.tsx` still shows the two currency gaps
 * disabled, the one place a purchaser can see them.
 *
 * `purchaser` is absent on purpose — it has its own endpoint, applied by `createEnquiryFlow` below.
 */
export function buildCreateEnquiryWireBody(header: NewEnquiryHeader | undefined, transactionDate: string): Record<string, unknown> {
  return {
    source: header?.source ?? 'manual',
    // `useEnquiries.ts`'s own `toEnquiryType` guard notes the server's `enquiry_type` Select
    // only ever emits `Provision`/`Technical` — defaulting a CREATE to `Commercial` (a value
    // the server's own field never offers) risked a 422 on the exact field this default was
    // supposed to keep valid. `Technical` matches the old bespoke mock's own default.
    enquiry_type: header?.enquiry_type ?? 'Technical',
    customer: header?.customer ?? '',
    transaction_date: transactionDate,
    // Sent only when the caller has one. An empty string is not a valid `Link → Company` and the
    // field is `reqd: 0`, so omitting it leaves the enquiry company-less (recoverable on the RFQ
    // header card) rather than failing the create outright.
    ...(header?.company ? { company: header.company } : {}),
    delivery_location: header?.delivery_location ?? null,
    // The location's selling price list, when the caller resolved one (2026-09-17).
    ...(header?.price_list ? { price_list: header.price_list } : {}),
    vessel: header?.vessel ?? null,
    port_of_delivery: header?.delivery_port ?? null,
    mail_id: header?.mail_id ?? null,
    ...(header?.enquiry_remarks ? { enquiry_remarks: header.enquiry_remarks } : {}),
    // Accepted in the create body since 2026-09-21 (measured); `assign_purchaser` still runs
    // after, for a server that ignores it here.
    ...(header?.purchaser ? { purchaser: header.purchaser } : {}),
    items: (header?.items ?? []).map(toNewItemWireRow),
  }
}

const reason = (error: unknown) => (error instanceof Error ? error.message : String(error))

export async function createEnquiryFlow(
  header: NewEnquiryHeader | undefined,
  transactionDate: string,
  api: EnquiryWriteApi,
): Promise<{ enquiry: Enquiry; warning?: string }> {
  const body = buildCreateEnquiryWireBody(header, transactionDate)
  const created = await api.callPost<WireCreateEnquiryResult>('enquiry', 'create_enquiry', { data: body })

  const name = created?.name ?? created?.data?.name
  // A 200 with no docname is a failed create, not a follow-up problem: there is nothing to open,
  // nothing to warn about, and nothing for the caller to navigate to. This still throws.
  if (!name) throw new Error('enquiry.create_enquiry did not return a name')

  /* ================= past this line the enquiry EXISTS — nothing may throw ================= */

  let warning: string | undefined

  if (header?.purchaser) {
    try {
      await api.callPost('enquiry', 'assign_purchaser', { enquiry: name, purchaser: header.purchaser })
    } catch (error) {
      warning = `Created ${name}, but could not assign ${header.purchaser} as purchaser (${reason(error)}). Set it on the RFQ page.`
    }
  }

  try {
    const detail = await api.callGet<WireEnquiryDetail>('enquiry', 'get_enquiry', { name })
    return { enquiry: toEnquiry(detail), warning }
  } catch (error) {
    // The document is made, so the read-back failing must not read as a failed create. Rebuilt
    // from the body we just sent — every key there is already a `get_enquiry` field name, so what
    // the user typed survives — with the docname from the create. The id is the part that has to
    // be right: it is what the caller navigates to, and that screen fetches the document itself.
    // Lines are left out rather than half-mapped from create-shaped rows that carry no
    // `enquiry_row_no`; the warning sends the user somewhere that has them.
    const fallback = { ...body, ...created, name, items: [] } as unknown as WireEnquiryDetail
    return {
      enquiry: toEnquiry(fallback),
      // An assignment failure outranks this one: it is the one with something to do about it.
      warning: warning ?? `Created ${name}, but could not read it back (${reason(error)}). Open it from the board.`,
    }
  }
}
